import { useEffect, useRef } from "react";
import { Minus, Plus, X, Tag, ScanBarcode } from "lucide-react";
import { bpToPct, fmtExpiry, formatMoney, pickLabel } from "@/lib/money";

function QtyStepper({ row, onQty }) {
  return (
    <div className="flex items-center rounded-lg border border-slate-200 bg-white">
      <button onClick={() => onQty(row.product_id, row.qty - 1)} disabled={row.qty <= 1} className="grid h-9 w-9 place-items-center text-slate-600 disabled:opacity-30" data-testid={`cart-qty-dec-${row.product_id}`} aria-label="Decrease">
        <Minus className="h-3.5 w-3.5" />
      </button>
      <input
        type="number"
        min={1}
        value={row.qty}
        onChange={(e) => {
          const n = parseInt(e.target.value, 10);
          if (n >= 1) onQty(row.product_id, n);
        }}
        onFocus={(e) => e.target.select()}
        data-testid={`cart-qty-input-${row.product_id}`}
        className="money h-9 w-11 border-x border-slate-200 bg-transparent text-center text-sm font-bold outline-none focus:bg-amber-50"
      />
      <button onClick={() => onQty(row.product_id, row.qty + 1)} className="grid h-9 w-9 place-items-center text-slate-600" data-testid={`cart-qty-inc-${row.product_id}`} aria-label="Increase">
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function Line({ row, biz, m, onQty, onRemove, onDiscount, canDiscount }) {
  const c = row.calc;
  return (
    <div className="line-in border-b border-dashed border-slate-200 py-3 last:border-0" data-testid={`cart-line-${row.product_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[13.5px] font-semibold leading-snug text-slate-900" data-testid={`cart-line-name-${row.product_id}`}>{row.product.name}</div>
          <div className="money mt-0.5 text-[11px] text-slate-500">
            {row.qty} × {m(c.gross / row.qty)}
          </div>
        </div>
        <div className="text-right">
          <div className="money text-[14px] font-bold text-slate-900" data-testid={`cart-line-total-${row.product_id}`}>{m(c.net)}</div>
          {c.line_discount > 0 && <div className="money text-[11px] text-emerald-700 line-through decoration-emerald-700/40">{m(c.gross)}</div>}
        </div>
      </div>
      {row.allocations.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`cart-line-batches-${row.product_id}`}>
          {row.allocations.map((a) =>
            a.location_code ? (
              <span key={a.lot_id || a.location_code} className="rounded bg-sky-100 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-sky-900" data-testid={`cart-line-pick-${row.product_id}-${a.location_code}`}>
                {pickLabel(a)} · ×{a.qty}
              </span>
            ) : (
              <span key={a.batch_id} className="rounded bg-amber-100 px-1.5 py-0.5 font-mono text-[10px] font-medium text-amber-900" data-testid={`cart-line-batch-${row.product_id}-${a.batch_no}`}>
                B#{a.batch_no} · EXP {fmtExpiry(a.expiry)} · ×{a.qty}
              </span>
            )
          )}
        </div>
      )}
      <div className="mt-2 flex items-center gap-1.5">
        <QtyStepper row={row} onQty={onQty} />
        {canDiscount && <button
          onClick={() => onDiscount(row)}
          data-testid={`cart-line-discount-button-${row.product_id}`}
          className={`flex h-9 items-center gap-1 rounded-lg px-2.5 text-[12px] font-semibold transition-colors ${row.discount ? "bg-emerald-100 text-emerald-800" : "text-slate-500 hover:bg-slate-100"}`}
        >
          <Tag className="h-3.5 w-3.5" />
          {row.discount ? (row.discount.type === "pct" ? `-${row.discount.value}%` : `-${m(c.line_discount)}`) : "Disc"}
        </button>}
        <button onClick={() => onRemove(row.product_id)} data-testid={`cart-line-remove-${row.product_id}`} className="ml-auto grid h-9 w-9 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600" aria-label="Remove">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export function CartTotals({ totals, biz, m, cartDiscount, onCartDiscount, canDiscount }) {
  const label = biz.tax_label;
  return (
    <div className="space-y-1.5 text-[13px]" data-testid="cart-totals">
      <div className="flex justify-between text-slate-600"><span>Subtotal</span><span className="money" data-testid="cart-subtotal">{m(totals.gross_total)}</span></div>
      {totals.line_discount_total > 0 && (
        <div className="flex justify-between text-emerald-700"><span>Item discounts</span><span className="money" data-testid="cart-line-discounts">-{m(totals.line_discount_total)}</span></div>
      )}
      {canDiscount && <div className="flex items-center justify-between text-slate-600">
        <button onClick={onCartDiscount} className="-ml-1 flex h-8 items-center gap-1 rounded-md px-1 font-semibold text-slate-700 underline decoration-dashed underline-offset-4 hover:text-slate-950" data-testid="cart-discount-button">
          <Tag className="h-3.5 w-3.5" /> Cart discount{cartDiscount?.type === "pct" ? ` ${cartDiscount.value}%` : ""}
        </button>
        <span className={`money ${totals.cart_discount ? "text-emerald-700" : ""}`} data-testid="cart-discount-amount">{totals.cart_discount ? `-${m(totals.cart_discount)}` : "add"}</span>
      </div>}
      {totals.tax_breakdown.map((t) => (
        <div key={t.rate_bp} className="flex justify-between text-slate-600" data-testid={`cart-tax-${t.rate_bp}`}>
          <span>{label} {bpToPct(t.rate_bp)}% {biz.tax_inclusive ? "(incl.)" : "(added)"}</span>
          <span className="money">{biz.tax_inclusive ? "" : "+"}{m(t.amount)}</span>
        </div>
      ))}
      <div className="dash-rule !my-2.5" />
      <div className="flex items-baseline justify-between">
        <span className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-slate-900">Total {biz.currency}</span>
        <span className="money text-[26px] font-extrabold leading-none text-slate-950" data-testid="cart-total">{m(totals.total)}</span>
      </div>
    </div>
  );
}

export function CartPanel({ cart, biz, onCharge, onLineDiscount, onCartDiscount, onToast, receiptPreviewNo, canDiscount, branchName }) {
  const m = (n) => formatMoney(n, biz.decimals);
  const endRef = useRef(null);
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "nearest" });
  }, [cart.rows.length]);
  const empty = cart.rows.length === 0;
  const onQty = (pid, q) => {
    const err = cart.setQty(pid, q);
    if (err) onToast(err);
  };
  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="cart-panel">
      <div className="slot mx-3 mt-4 shrink-0" />
      <div className="tape mx-5 mb-4 flex min-h-0 flex-1 flex-col px-4 pb-4 pt-4">
        <div className="shrink-0 text-center">
          <div className="font-mono text-[11px] font-bold uppercase tracking-[0.22em] text-slate-900">{biz.name}</div>
          <div className="mt-0.5 font-mono text-[10px] text-slate-400" data-testid="cart-branch-next-receipt">{branchName} · Next {receiptPreviewNo}</div>
          <div className="dash-rule my-3" />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto pr-1" data-testid="cart-lines">
          {empty ? (
            <div className="flex h-full min-h-[140px] flex-col items-center justify-center gap-2 text-center text-slate-400" data-testid="cart-empty">
              <ScanBarcode className="h-8 w-8" />
              <div className="text-sm font-medium">Scan or tap a product</div>
              <div className="font-mono text-[10px] uppercase tracking-widest">Scanner is listening</div>
            </div>
          ) : (
            cart.rows.map((r) => (
              <Line key={r.product_id} row={r} biz={biz} m={m} onQty={onQty} onRemove={cart.remove} onDiscount={onLineDiscount} canDiscount={canDiscount} />
            ))
          )}
          <div ref={endRef} />
        </div>
        {cart.totals && (
          <div className="shrink-0 pt-2">
            <div className="dash-rule mb-3" />
            <CartTotals totals={cart.totals} biz={biz} m={m} cartDiscount={cart.cartDiscount} onCartDiscount={onCartDiscount} canDiscount={canDiscount} />
          </div>
        )}
      </div>
      <div className="shrink-0 px-4 pb-4">
        <button
          onClick={onCharge}
          disabled={empty}
          data-testid="charge-button"
          className="group flex h-14 w-full items-center justify-between rounded-xl bg-amber-500 px-5 font-display text-lg font-bold text-slate-950 shadow-[0_10px_30px_-10px_rgba(245,158,11,0.7)] transition-[background-color,transform] hover:bg-amber-400 active:scale-[0.99] disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-500 disabled:shadow-none"
        >
          <span className="flex items-center gap-2">Charge <kbd className="rounded bg-black/10 px-1.5 py-0.5 font-mono text-[10px] font-semibold">F9</kbd></span>
          <span className="money">{cart.totals ? m(cart.totals.total) : m(0)}</span>
        </button>
      </div>
    </div>
  );
}
